// Building how-to cards: a short "how it works" card the first time each building type unlocks (an unlock
// whose version-active effects include `build.<type>`), re-openable from the building's card (the "?" button).
// The text is copy.json `howto.<type>1…n` (steps in order, the first gap ends the list) plus `howto.ghost` when
// the type's first build is a ghost (R10) and none was built yet. PURE.
import type { CopyData } from '../data/schema.ts';
import type { BuildingBase, SimDefs } from '../sim/defs.ts';
import type { BType } from '../sim/gen/ids.ts';

/** The most steps a card shows (copy keys beyond it are ignored). */
const MAX_STEPS = 6;

/** The building types an unlock's effects make buildable (`build.<type>`), in effect order. */
export function howtoTypesOf(effects: readonly string[]): BType[] {
  const out: BType[] = [];
  for (const e of effects) if (e.startsWith('build.')) out.push(e.slice('build.'.length) as BType);
  return out;
}

/**
 * The type's how-to steps; empty when copy.json has none (no card, no "?" button). `built`: one was already
 * built (no ghost line then).
 */
export function howtoSteps(copy: CopyData, defs: SimDefs, type: BType, built: boolean): string[] {
  const sec = copy.howto;
  const out: string[] = [];
  for (let i = 1; i <= MAX_STEPS; i++) {
    const t = sec[`${type}${i}`];
    if (t === undefined) break;
    out.push(t);
  }
  if (out.length === 0) return out;
  const b = (defs.buildings as Partial<Record<BType, BuildingBase>>)[type];
  if (!built && b !== undefined && b.ghost.length > 0 && sec.ghost !== undefined)
    out.push(sec.ghost);
  return out;
}

/** The uiFlags key that records a type's card as shown. */
export function howtoFlag(type: BType): `howto_${string}` {
  return `howto_${type}`;
}
